import { useMemo } from 'react';

import { TypedPolicy } from '../../../types';

import {
  KubernetesRegistryFormValues,
  KubernetesRegistryPolicyPayload,
  KUBERNETES_REGISTRY_INITIAL_VALUES,
} from './types';

/**
 * Hook to get initial values for Kubernetes Registry Policy form
 *
 * @param policy - Optional policy to load values from (for edit mode)
 * @returns Form initial values including common policy config fields
 */
export function useKubernetesRegistryInitialValues(
  policy?: TypedPolicy<unknown>
): KubernetesRegistryFormValues {
  return useMemo(() => {
    // Create mode: use defaults
    if (!policy) {
      return KUBERNETES_REGISTRY_INITIAL_VALUES;
    }

    // Edit mode: deserialize from policy data
    const data = policy.Data;
    const accessRows: KubernetesRegistryFormValues['accessRows'] = [];

    if (data && typeof data === 'object' && 'RegistryAccessPolicies' in data) {
      const policyData = data as KubernetesRegistryPolicyPayload;

      // Convert per-registry structure to form rows
      const regPolicies = policyData.RegistryAccessPolicies;
      if (regPolicies) {
        Object.entries(regPolicies).forEach(
          ([registryIdStr, registryPolicy]) => {
            const registryId = parseInt(registryIdStr, 10);
            const namespaces =
              registryPolicy.Namespaces?.map((ns) => ns.value) || [];

            accessRows.push({
              registryId,
              namespaces,
            });
          }
        );
      }
    }

    return {
      name: policy.Name,
      environmentGroupIds: policy.EnvironmentGroups ?? [],
      accessRows,
    };
  }, [policy]);
}
